import { StyleDeclarationValue, css } from 'aphrodite';
import queryString from 'query-string';
import * as React from 'react';
import { useHistory, useLocation } from 'react-router';
import { ILocationState } from '../../../models';
import { MastheadPortalId } from '../../../models/AdminModels';
import { useAdminUserSession } from '../../../models/hooks/adminStateHooks';
import { useFullscreenModal } from '../../../models/hooks/appStateHooks';
import { AdminPermission, QuickSightDashboardViewModel } from '../../../viewmodels/AdminViewModels';
import { Modal } from '../../../web/components/Modal';
import { NavigationBreadcrumbsBar } from '../../../web/components/NavigationBreadcrumbsBar';
import { Portal } from '../../../web/components/Portal';
import { AIDADialsIcon } from '../../../web/components/svgs/graphics/AIDADialsIcon';
import { AutoEmailConfigIcon } from '../../../web/components/svgs/graphics/AutoEmailConfigIcon';
import { BookOfBusinessHealthIcon } from '../../../web/components/svgs/graphics/BookOfBusinessHealthIcon';
import { CSMAccountsIcon } from '../../../web/components/svgs/graphics/CSMAccountsIcon';
import { GlobeSearchIcon } from '../../../web/components/svgs/icons/GlobeSearchIcon';
import { PhoneNumberInfoIcon } from '../../../web/components/svgs/icons/PhoneNumberInfoIcon';
import { SalesKPIsIcon } from '../../../web/components/svgs/icons/SalesKPIsIcon';
import { SelectingTeamIcon } from '../../../web/components/svgs/icons/SelectingTeamIcon';
import { SuccessKPIsIcon } from '../../../web/components/svgs/icons/SuccessKPIsIcon';
import { PricingWizardIcon } from '../../../web/components/svgs/icons/PricingWizardIcon';
import { AutoDiscoverEmailModal } from '../../components/tools/AutoDiscoverEmailModal';
import { PhoneNumberInfoModal } from '../../components/tools/PhoneNumberInfoModal';
import { ToolsCard } from '../../components/tools/ToolsCard';
import { AddContactForm } from './presentation';
import { styleSheet } from './styles';

interface IProps {
	className?: string;
	styles?: StyleDeclarationValue[];
}

export function Tools({ className, styles }: IProps) {
	const userSession = useAdminUserSession();
	const fullscreenModal = useFullscreenModal();
	const location = useLocation();
	const history = useHistory();
	const [isAutoDiscoverEmailModalOpen, setIsAutoDiscoverEmailModalOpen] = React.useState(false);
	const [addContactModalOpen, setAddContactModalOpen] = React.useState(false);
	const [phoneNumberInfoModalOpen, setPhoneNumberInfoModalOpen] = React.useState(false);
	const launchQuickSightModal = React.useCallback(
		(dashboardId: string, params?: Record<string, string>) => {
			const viewModel = new QuickSightDashboardViewModel(userSession, dashboardId, null, params);
			const locationState: ILocationState<QuickSightDashboardViewModel, any> = {
				viewModel,
			};

			fullscreenModal.history.push('/quicksight/dashboard', locationState);
		},
		[fullscreenModal, userSession]
	);
	const handleCloseAddContactModal = () => setAddContactModalOpen(false);
	React.useEffect(() => {
		const queryParams = queryString.parse(location.search);
		if (!queryParams.tool) {
			return;
		}
		let dbId: string;
		switch (queryParams.tool?.toLowerCase()) {
			case 'customersbylocation':
				dbId = queryParams.dashboardId || QuickSightDashboardViewModel.CustomerMapId;
				break;
			case 'saleskpis':
				dbId = queryParams.dashboardId || QuickSightDashboardViewModel.SalesKPIsId;
				break;
			case 'successkpis':
				dbId = queryParams.dashboardId || QuickSightDashboardViewModel.SuccessKPIsId;
				break;
			default:
				break;
		}

		if (dbId) {
			launchQuickSightModal(dbId, queryParams);
		}
	}, [location.search, launchQuickSightModal]);
	return (
		<div className={`${css(styleSheet.container, ...(styles || []))} tools-container ${className || ''}`}>
			<Portal destination={MastheadPortalId}>
				<NavigationBreadcrumbsBar currentLocationName='Tools' />
			</Portal>
			<div className={css(styleSheet.cards)}>
				<div className={css(styleSheet.cardsContent)}>
					<ToolsCard
						image={<PricingWizardIcon className={css(styleSheet.icon)} />}
						onClick={() => history.push('/tools/pricing-wizard')}
						title='Pricing Wizard'
					>
						{['Calculate pricing and generate proposals']}
					</ToolsCard>
					<ToolsCard
						image={<AutoEmailConfigIcon className={css(styleSheet.icon)} />}
						onClick={() => setIsAutoDiscoverEmailModalOpen(true)}
						title='Auto Discover'
					>
						{['Auto discover email configuration']}
					</ToolsCard>
					<ToolsCard
						image={<GlobeSearchIcon className={css(styleSheet.icon)} />}
						onClick={() => launchQuickSightModal(QuickSightDashboardViewModel.CustomerMapId)}
						title='Customers by Location'
					>
						{['View customer location grouping on a map']}
					</ToolsCard>
					<ToolsCard
						image={<AIDADialsIcon className={css(styleSheet.icon)} />}
						onClick={() => launchQuickSightModal(QuickSightDashboardViewModel.AIDARealMagicDialsId)}
						title='Dials'
					>
						{['View dial performance']}
					</ToolsCard>
					<ToolsCard
						image={<SalesKPIsIcon className={css(styleSheet.icon)} />}
						onClick={() => launchQuickSightModal(QuickSightDashboardViewModel.SalesKPIsId)}
						title='Sales KPIs'
					>
						{['View key performance indicators for sales']}
					</ToolsCard>
					<ToolsCard
						image={<SuccessKPIsIcon className={css(styleSheet.icon)} />}
						onClick={() => launchQuickSightModal(QuickSightDashboardViewModel.SuccessKPIsId)}
						title='Success KPIs'
					>
						{['View key performance indicators for success']}
					</ToolsCard>
					<ToolsCard
						image={<CSMAccountsIcon className={css(styleSheet.icon)} />}
						onClick={() => launchQuickSightModal(QuickSightDashboardViewModel.CSMAccountDetailsId)}
						title='CSM Accounts'
					>
						{['View account performance by CSM']}
					</ToolsCard>
					<ToolsCard
						image={<BookOfBusinessHealthIcon className={css(styleSheet.icon)} />}
						onClick={() => launchQuickSightModal(QuickSightDashboardViewModel.BookOfBusinessHealthId)}
						title='Book of Business Health'
					>
						{['Book of Business Health']}
					</ToolsCard>
					<ToolsCard
						image={<PhoneNumberInfoIcon className={css(styleSheet.icon)} />}
						onClick={() => setPhoneNumberInfoModalOpen(true)}
						title='Phone Number Information'
					>
						{['Get details on a phone number']}
					</ToolsCard>
					{userSession.hasPermission(AdminPermission.ModifyAccount) ? (
						<ToolsCard
							image={<SelectingTeamIcon className={css(styleSheet.icon)} />}
							onClick={() => setAddContactModalOpen(true)}
							title='Add Contact Name'
						>
							{["Add new name to Levitate's name database"]}
						</ToolsCard>
					) : null}
				</div>
			</div>
			{isAutoDiscoverEmailModalOpen ? (
				<AutoDiscoverEmailModal onRequestClose={() => setIsAutoDiscoverEmailModalOpen(false)} />
			) : null}
			{phoneNumberInfoModalOpen ? (
				<PhoneNumberInfoModal onRequestClose={() => setPhoneNumberInfoModalOpen(false)} />
			) : null}
			<Modal isOpen={addContactModalOpen} onRequestClose={handleCloseAddContactModal} useDefaultHeader={true}>
				<AddContactForm onCancelClick={handleCloseAddContactModal} />
			</Modal>
		</div>
	);
}
